import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Card } from '../Card/Card';

const filterProps = (
  ...keys: string[]
): { shouldForwardProp: (prop: string) => boolean } => ({
  shouldForwardProp: (prop: string) => !keys.includes(prop),
});

export interface ContainerProps {
  children: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  centered?: boolean;
  padding?: boolean;
  className?: string;
}

const getMaxWidth = (maxWidth: ContainerProps['maxWidth']): string => {
  const widths = {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    full: '100%',
  };

  return widths[maxWidth || 'full'];
};

export const Container = styled.div.withConfig(
  filterProps('maxWidth', 'centered', 'padding'),
)<ContainerProps>`
  width: 100%;
  max-width: ${({ maxWidth }) => getMaxWidth(maxWidth)};
  margin: ${({ centered }) => (centered ? '0 auto' : '0')};
  padding: ${({ padding, theme }) =>
    padding ? `${theme.spacing[4]} ${theme.spacing[4]}` : '0'};
  // min-height: 100%;
  display: flex;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ padding, theme }) =>
      padding ? `${theme.spacing[4]} ${theme.spacing[6]}` : '0'};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ padding, theme }) =>
      padding ? `${theme.spacing[4]} ${theme.spacing[8]}` : '0'};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ padding, theme }) =>
      padding ? `${theme.spacing[4]} ${theme.spacing[12]}` : '0'};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: ${({ padding, theme }) =>
      padding ? `${theme.spacing[4]} ${theme.spacing[16]}` : '0'};
  }
`;

// Enhanced Container for main page content
export const PageContainer = styled(Container)`
  flex: 1;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[6]}`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[8]}`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[12]}`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[20]}`};
  }
`;

// Responsive Card for main content areas
export const ResponsiveCard = styled(Card)`
  width: 100%;
  max-width: none;
  margin: 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin: 0;
  }
  .MuiDataGrid-root {
    min-height: 600px;
    height: auto;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      min-height: 700px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      min-height: 800px;
    }
  }
`;

export interface GridProps {
  children: ReactNode;
  columns?:
    | number
    | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  gap?: string | number;
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  justifyItems?: 'start' | 'center' | 'end' | 'stretch';
  className?: string;
}

export const Grid = styled.div.withConfig(
  filterProps('columns', 'gap', 'alignItems', 'justifyItems'),
)<GridProps>`
  display: grid;
  gap: ${({ gap, theme }) =>
    typeof gap === 'number' ? theme.spacing[gap] : gap || theme.spacing[4]};
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  justify-items: ${({ justifyItems }) => justifyItems || 'stretch'};

  ${({ columns }) => {
    if (typeof columns === 'number') {
      return css`
        grid-template-columns: repeat(${columns}, 1fr);
      `;
    }

    if (typeof columns === 'object') {
      return css`
        grid-template-columns: repeat(${columns.xs || 1}, 1fr);

        @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
          grid-template-columns: repeat(${columns.sm || columns.xs || 1}, 1fr);
        }

        @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
          grid-template-columns: repeat(
            ${columns.md || columns.sm || columns.xs || 1},
            1fr
          );
        }

        @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
          grid-template-columns: repeat(
            ${columns.lg || columns.md || columns.sm || columns.xs || 1},
            1fr
          );
        }

        @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
          grid-template-columns: repeat(
            ${columns.xl ||
            columns.lg ||
            columns.md ||
            columns.sm ||
            columns.xs ||
            1},
            1fr
          );
        }
      `;
    }

    return css`
      grid-template-columns: repeat(1, 1fr);
    `;
  }}
`;

export interface FlexProps {
  children: ReactNode;
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  align?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
  gap?: string | number;
  fullWidth?: boolean;
  fullHeight?: boolean;
  className?: string;
}

const getJustifyContent = (justify: FlexProps['justify']): string => {
  const justifyMap = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
    around: 'space-around',
    evenly: 'space-evenly',
  };

  return justifyMap[justify || 'start'];
};

const getAlignItems = (align: FlexProps['align']): string => {
  const alignMap = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    stretch: 'stretch',
    baseline: 'baseline',
  };

  return alignMap[align || 'stretch'];
};

export const Flex = styled.div.withConfig(
  filterProps(
    'direction',
    'wrap',
    'justify',
    'align',
    'gap',
    'fullWidth',
    'fullHeight',
  ),
)<FlexProps>`
  display: flex;
  flex-direction: ${({ direction }) => direction || 'row'};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  justify-content: ${({ justify }) => getJustifyContent(justify)};
  align-items: ${({ align }) => getAlignItems(align)};
  gap: ${({ gap, theme }) =>
    typeof gap === 'number' ? theme.spacing[gap] : gap || '0'};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  height: ${({ fullHeight }) => (fullHeight ? '100%' : 'auto')};
`;

export interface StackProps {
  children: ReactNode;
  spacing?: string | number;
  direction?: 'vertical' | 'horizontal';
  divider?: boolean;
  className?: string;
}

export const Stack = styled.div.withConfig(
  filterProps('spacing', 'direction', 'divider'),
)<StackProps>`
  display: flex;
  flex-direction: ${({ direction }) =>
    direction === 'horizontal' ? 'row' : 'column'};
  gap: ${({ spacing, theme }) =>
    typeof spacing === 'number'
      ? theme.spacing[spacing]
      : spacing || theme.spacing[4]};

  ${({ divider, direction, theme }) =>
    divider &&
    css`
      > *:not(:last-child) {
        ${direction === 'horizontal'
          ? css`
              border-right: 1px solid ${theme.colors.divider};
              padding-right: ${theme.spacing[4]};
              margin-right: ${theme.spacing[4]};
            `
          : css`
              border-bottom: 1px solid ${theme.colors.divider};
              padding-bottom: ${theme.spacing[4]};
              margin-bottom: ${theme.spacing[4]};
            `}
      }
    `}
`;

export interface BoxProps {
  children: ReactNode;
  padding?: string | number;
  margin?: string | number;
  className?: string;
}

export const Box = styled.div.withConfig(
  filterProps('padding', 'margin'),
)<BoxProps>`
  padding: ${({ padding, theme }) =>
    typeof padding === 'number' ? theme.spacing[padding] : padding || '0'};
  margin: ${({ margin, theme }) =>
    typeof margin === 'number' ? theme.spacing[margin] : margin || '0'};
`;

export interface SpacerProps {
  size?: string | number;
  axis?: 'horizontal' | 'vertical';
}

export const Spacer = styled.div.withConfig(
  filterProps('size', 'axis'),
)<SpacerProps>`
  ${({ axis, size, theme }) => {
    const spacing =
      typeof size === 'number' ? theme.spacing[size] : size || theme.spacing[4];

    if (axis === 'horizontal') {
      return css`
        width: ${spacing};
        height: 1px;
      `;
    }

    return css`
      width: 1px;
      height: ${spacing};
    `;
  }}
`;

export const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.divider};
  margin: ${({ theme }) => theme.spacing[4]} 0;
  width: 100%;
`;
